import React, { useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  ArrowRight, FileText, Mic, Brain, Layers, Sparkles, Shield, Globe,
  Upload, MessageSquare, Download, Check, ChevronDown,
} from "lucide-react";
import { Button } from "../components/ui/button";
import BrandLogo from "../components/BrandLogo";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { useBranding } from "../context/BrandingContext";
import { useContentValue } from "../context/ContentContext";
import Footer from "../components/Footer";

export default function Landing() {
  const { t } = useTranslation();
  const branding = useBranding();
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="landing-page">
      <Header />
      <Hero />
      <BuiltFor />
      <HowItWorks />
      <FeatureGrid />
      <FAQ />
      <FinalCTA />
      <Footer />
    </div>
  );
}

function Header() {
  const { t } = useTranslation();
  return (
    <header className="border-b border-border bg-background/95 backdrop-blur sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          <BrandLogo size="lg" />
        </Link>
        <nav className="hidden md:flex items-center gap-7 text-sm">
          <Link to="/features" className="text-muted-foreground hover:text-foreground font-medium" data-testid="nav-features">{t("nav.features")}</Link>
          <Link to="/pricing" className="text-muted-foreground hover:text-foreground font-medium" data-testid="nav-pricing">{t("nav.pricing")}</Link>
        </nav>
        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <Link to="/login"><Button variant="ghost" className="rounded-sm" data-testid="cta-signin">{t("cta.sign_in")}</Button></Link>
          <Link to="/signup"><Button className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90" data-testid="cta-get-started-header">{t("cta.get_started")}</Button></Link>
        </div>
      </div>
    </header>
  );
}

function Hero() {
  const { t } = useTranslation();
  const eyebrow = useContentValue("landing.hero.eyebrow", "[ IA para emprendedores y contadores ]");
  const headline = useContentValue("landing.hero.headline", "Tu segundo cerebro para documentos, datos y decisiones.");
  const sub = useContentValue("landing.hero.subheadline", "");
  const ctaPrimary = useContentValue("landing.hero.cta_primary", t("cta.get_started"));
  const ctaSecondary = useContentValue("landing.hero.cta_secondary", t("cta.see_pricing"));
  const note = useContentValue("landing.hero.note", "");

  return (
    <section className="relative overflow-hidden border-b border-border" data-testid="hero-section">
      {/* Animated gradient background */}
      <div className="absolute inset-0 -z-10 opacity-[0.07]"
           style={{background: "radial-gradient(ellipse at 30% 30%, hsl(var(--primary)) 0%, transparent 60%), radial-gradient(ellipse at 80% 70%, #f0c14b 0%, transparent 60%)"}} />
      <div className="absolute inset-0 -z-10 grid-bg opacity-[0.4]" />

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div initial={{opacity:0, y:24}} animate={{opacity:1, y:0}} transition={{duration:0.6}}>
          <div className="text-xs uppercase tracking-[0.2em] font-mono text-primary font-bold" data-testid="hero-eyebrow">{eyebrow}</div>
          <h1 className="mt-4 font-display font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]" data-testid="hero-headline">
            {headline}
          </h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-xl leading-relaxed" data-testid="hero-sub">{sub}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/signup"><Button size="lg" className="rounded-sm h-12 px-6 bg-primary text-primary-foreground hover:bg-primary/90 text-base" data-testid="cta-primary">
              {ctaPrimary} <ArrowRight className="h-4 w-4 ml-2" />
            </Button></Link>
            <Link to="/pricing"><Button size="lg" variant="outline" className="rounded-sm h-12 px-6 text-base" data-testid="cta-secondary">
              {ctaSecondary}
            </Button></Link>
          </div>
          {note && <p className="mt-4 text-xs font-mono text-muted-foreground" data-testid="hero-note">{note}</p>}
        </motion.div>

        {/* Animated illustration: document → AI → output flow */}
        <HeroIllustration />
      </div>
    </section>
  );
}

function HeroIllustration() {
  return (
    <motion.div initial={{opacity:0, scale:0.95}} animate={{opacity:1, scale:1}} transition={{duration:0.7, delay:0.2}}
                className="relative aspect-square max-w-[480px] mx-auto" data-testid="hero-illustration">
      <div className="absolute inset-0 bg-primary/5 rounded-2xl" />

      {/* Floating document card */}
      <motion.div
        initial={{y:0}}
        animate={{y:[-6, 6, -6]}}
        transition={{duration:4, repeat:Infinity, ease:"easeInOut"}}
        className="absolute top-8 left-8 w-44 bg-card border border-border rounded-md shadow-xl p-4"
      >
        <FileText className="h-5 w-5 text-primary mb-3" />
        <div className="space-y-1.5">
          <div className="h-1.5 bg-muted rounded-sm w-full" />
          <div className="h-1.5 bg-muted rounded-sm w-4/5" />
          <div className="h-1.5 bg-muted rounded-sm w-2/3" />
        </div>
        <div className="mt-3 text-[10px] font-mono text-muted-foreground">factura-2026.pdf</div>
      </motion.div>

      {/* Center sparkle (AI) */}
      <motion.div
        animate={{rotate:[0,360]}}
        transition={{duration:20, repeat:Infinity, ease:"linear"}}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-24 w-24 rounded-full bg-primary grid place-items-center shadow-2xl"
      >
        <Sparkles className="h-10 w-10 text-primary-foreground" />
      </motion.div>

      {/* Floating chat reply */}
      <motion.div
        initial={{y:0}}
        animate={{y:[6, -6, 6]}}
        transition={{duration:4, repeat:Infinity, ease:"easeInOut", delay:0.5}}
        className="absolute bottom-8 right-8 w-52 bg-card border border-border rounded-md shadow-xl p-4"
      >
        <div className="flex items-center gap-2">
          <div className="h-2 w-2 rounded-full bg-emerald-500" />
          <div className="text-[10px] font-mono text-muted-foreground uppercase">Faro</div>
        </div>
        <div className="mt-2 space-y-1.5">
          <div className="h-1.5 bg-primary/30 rounded-sm w-full" />
          <div className="h-1.5 bg-primary/30 rounded-sm w-5/6" />
          <div className="h-1.5 bg-primary/30 rounded-sm w-3/4" />
        </div>
        <div className="mt-3 flex items-center gap-1 text-[10px] font-mono">
          <Check className="h-3 w-3 text-emerald-500" />
          <span className="text-muted-foreground">Total: USD 1.250</span>
        </div>
      </motion.div>
    </motion.div>
  );
}

function BuiltFor() {
  const title = useContentValue("landing.builtfor.title", "Pensado para tu día a día");
  const sub = useContentValue("landing.builtfor.subtitle", "");
  const cards = [
    {k: "solopreneur", icon: <Brain className="h-6 w-6" />},
    {k: "entrepreneur", icon: <Layers className="h-6 w-6" />},
    {k: "accountant", icon: <FileText className="h-6 w-6" />},
  ];
  return (
    <section className="border-b border-border bg-muted/20" data-testid="builtfor-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-display font-black tracking-tight text-3xl lg:text-4xl">{title}</h2>
          <p className="mt-3 text-muted-foreground">{sub}</p>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-5">
          {cards.map((c, i) => (
            <BuiltForCard key={c.k} {...c} idx={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function BuiltForCard({ k, icon, idx }) {
  const title = useContentValue(`landing.builtfor.${k}.title`, k);
  const body = useContentValue(`landing.builtfor.${k}.body`, "");
  return (
    <motion.div
      initial={{opacity:0, y:30}}
      whileInView={{opacity:1, y:0}}
      viewport={{once: true}}
      transition={{duration:0.5, delay: idx*0.1}}
      className="group relative bg-card border border-border rounded-md p-7 hover:border-primary/40 hover:shadow-lg transition-all"
      data-testid={`builtfor-${k}`}
    >
      <div className="h-12 w-12 grid place-items-center bg-primary/10 text-primary rounded-md mb-4 group-hover:scale-110 transition-transform">
        {icon}
      </div>
      <h3 className="font-display font-bold tracking-tight text-xl">{title}</h3>
      <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{body}</p>
    </motion.div>
  );
}

function HowItWorks() {
  const title = useContentValue("landing.how.title", "Cómo funciona");
  const sub = useContentValue("landing.how.subtitle", "");
  const steps = [
    {n: "1", icon: <Upload className="h-7 w-7" />, slug: "step1"},
    {n: "2", icon: <MessageSquare className="h-7 w-7" />, slug: "step2"},
    {n: "3", icon: <Download className="h-7 w-7" />, slug: "step3"},
  ];
  return (
    <section className="border-b border-border" data-testid="how-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-display font-black tracking-tight text-3xl lg:text-4xl">{title}</h2>
          <p className="mt-3 text-muted-foreground">{sub}</p>
        </div>
        <div className="mt-12 relative">
          {/* Decorative line */}
          <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
          <div className="grid md:grid-cols-3 gap-6 relative">
            {steps.map((s, i) => (
              <StepCard key={s.slug} {...s} idx={i} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function StepCard({ n, icon, slug, idx }) {
  const title = useContentValue(`landing.how.${slug}.title`, `${n}.`);
  const body = useContentValue(`landing.how.${slug}.body`, "");
  return (
    <motion.div
      initial={{opacity:0, y:24}} whileInView={{opacity:1, y:0}}
      viewport={{once:true}} transition={{duration:0.5, delay: idx*0.15}}
      className="relative text-center" data-testid={`step-${slug}`}
    >
      <div className="mx-auto h-24 w-24 rounded-full bg-primary text-primary-foreground grid place-items-center shadow-lg">{icon}</div>
      <h3 className="mt-5 font-display font-bold tracking-tight text-xl">{title}</h3>
      <p className="mt-2 text-sm text-muted-foreground max-w-xs mx-auto">{body}</p>
    </motion.div>
  );
}

function FeatureGrid() {
  const title = useContentValue("landing.features.title", "Todo lo que necesitás en una sola plataforma");
  const items = [
    {k: "docs", icon: <FileText className="h-5 w-5" />},
    {k: "voice", icon: <Mic className="h-5 w-5" />},
    {k: "workspace", icon: <Layers className="h-5 w-5" />},
    {k: "multimodel", icon: <Brain className="h-5 w-5" />},
    {k: "security", icon: <Shield className="h-5 w-5" />},
    {k: "bilingual", icon: <Globe className="h-5 w-5" />},
  ];
  return (
    <section className="border-b border-border bg-primary/[0.02]" data-testid="features-grid">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="font-display font-black tracking-tight text-3xl lg:text-4xl">{title}</h2>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it, i) => (
            <FeatureTile key={it.k} {...it} idx={i} />
          ))}
        </div>
        <div className="mt-10 text-center">
          <Link to="/features">
            <Button variant="outline" className="rounded-sm" data-testid="features-cta">
              Ver todas las funciones <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

function FeatureTile({ k, icon, idx }) {
  const title = useContentValue(`landing.features.${k}.title`, k);
  const body = useContentValue(`landing.features.${k}.body`, "");
  return (
    <motion.div
      initial={{opacity:0, y:16}} whileInView={{opacity:1, y:0}}
      viewport={{once:true}} transition={{duration:0.4, delay: idx*0.08}}
      className="bg-card border border-border rounded-md p-5 hover:border-primary/40 hover:bg-card transition" data-testid={`feature-tile-${k}`}
    >
      <div className="h-9 w-9 grid place-items-center bg-primary/10 text-primary rounded-md mb-3">{icon}</div>
      <h3 className="font-display font-bold tracking-tight">{title}</h3>
      <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{body}</p>
    </motion.div>
  );
}

function FAQ() {
  const title = useContentValue("landing.faq.title", "Preguntas frecuentes");
  const q1 = useContentValue("landing.faq.q1.q", "");
  const a1 = useContentValue("landing.faq.q1.a", "");
  const q2 = useContentValue("landing.faq.q2.q", "");
  const a2 = useContentValue("landing.faq.q2.a", "");
  const q3 = useContentValue("landing.faq.q3.q", "");
  const a3 = useContentValue("landing.faq.q3.a", "");
  const q4 = useContentValue("landing.faq.q4.q", "");
  const a4 = useContentValue("landing.faq.q4.a", "");
  const items = [
    {q: q1, a: a1}, {q: q2, a: a2}, {q: q3, a: a3}, {q: q4, a: a4},
  ].filter(x => x.q);
  const [open, setOpen] = React.useState(null);
  return (
    <section className="border-b border-border" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-6 lg:px-12 py-20">
        <h2 className="font-display font-black tracking-tight text-3xl lg:text-4xl text-center">{title}</h2>
        <div className="mt-10 space-y-3">
          {items.map((it, i) => (
            <div key={i} className="border border-border rounded-md bg-card overflow-hidden" data-testid={`faq-${i}`}>
              <button onClick={() => setOpen(open === i ? null : i)} className="w-full px-5 py-4 text-left flex items-center justify-between gap-4 hover:bg-muted/30 transition">
                <span className="font-semibold">{it.q}</span>
                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform flex-shrink-0 ${open === i ? "rotate-180" : ""}`} />
              </button>
              {open === i && <div className="px-5 pb-5 text-sm text-muted-foreground leading-relaxed">{it.a}</div>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  const { t } = useTranslation();
  const title = useContentValue("landing.cta.title", "Empezá hoy. Tus primeros 100 tokens son nuestros.");
  const sub = useContentValue("landing.cta.subtitle", "");
  return (
    <section className="bg-primary text-primary-foreground" data-testid="final-cta">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 text-center">
        <h2 className="font-display font-black tracking-tight text-3xl lg:text-5xl max-w-3xl mx-auto leading-tight">{title}</h2>
        <p className="mt-4 text-primary-foreground/80 max-w-xl mx-auto">{sub}</p>
        <div className="mt-8">
          <Link to="/signup">
            <Button size="lg" variant="secondary" className="rounded-sm h-12 px-8 text-base bg-white text-primary hover:bg-white/90" data-testid="final-cta-btn">
              {t("cta.get_started")} <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}
